import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { AppBar, Box, Button, Menu, MenuItem } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '../../api/auth.api';
import { useGetCurrentUserQuery } from '../../api/user.api';
import circadiaLogo from '../../assets/circadia-2025-white.png';
import { USER_MANAGEMENT } from '../../constants/messages';
import { useIsUserManagement } from '../../hooks/useIsUserManagement';
import { useUserStatus } from '../../hooks/useUserStatus.ts';
import { useIsUserInternal, useUserHasRoleScope } from '../../hooks/useUserType.ts';
import { ROUTE_FORGOT_PASSWORD, USER_MANAGEMENT_URI } from '../../router/router';
import AlertBar from '../AlertBar/AlertBar';
import { UserGuideModal } from '../Modals/UserGuideModal/UserGuideModal.tsx';
import MainNavBar from '../Navigation/MainNavBar/MainNavBar';
import MobileNavMenu from '../Navigation/MobileNavMenu';
import GeneralSearchBar from './GeneralSearchBar/GeneralSearchBar';

export default function Header() {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [language, setLanguage] = useState('en');
	const [isUserGuideModalOpen, setIsUserGuideModalOpen] = useState(false);
	const open = Boolean(anchorEl);
	const isTabletOrMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
	const navigate = useNavigate();
	const { isInactive } = useUserStatus();
	const location = useLocation();
	const isLoginPage = location.pathname === '/login';
	const isForgotPasswordPage = location.pathname === ROUTE_FORGOT_PASSWORD;
	const showActiveUserResource = !isInactive && !isLoginPage && !isForgotPasswordPage;
	const isUserManagement = useIsUserManagement();

	const { data: user } = useGetCurrentUserQuery();
	const isInternalUser = useIsUserInternal();
	const shouldShowUserManagement =
		useUserHasRoleScope('CIRCADIA_USER_MANAGE_ACCESS') && !isInternalUser; // NOTE: This logic was updated with roles work

	const [logout] = useLogoutMutation();

	const handleLogout = async () => {
		try {
			await logout().unwrap();
			await new Promise((resolve) => setTimeout(resolve, 500));
			window.location.href = '/'; // Or redirect to login if needed
		} catch (e) {
			// Optionally show error
			await new Promise((resolve) => setTimeout(resolve, 500));
			window.location.href = '/';
		}
	};

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = (lang?: string) => {
		setAnchorEl(null);
		if (lang && language !== lang) setLanguage(lang);
	};

	return (
		<>
			<AppBar
				elevation={2}
				sx={{
					backgroundColor: (theme) => theme.palette.brand.secondary,
					boxShadow: 'none',
					position: 'relative',
					width: '100%',
				}}
			>
				<Box
					sx={{
						maxWidth: '1600px',
						width: { xs: '100%', sm: '80%', md: '90%' },
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						px: 3,
						py: 2,
						m: '0 auto',
					}}
				>
					{/* Logo on the left, mobile menu on tablet/mobile */}
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						{isTabletOrMobile && <MobileNavMenu />}
						<img
							src={circadiaLogo}
							alt="Circadia Logo"
							style={{ height: 48, cursor: 'pointer' }}
							data-testid="circadia-logo"
							onClick={() => navigate('/')}
						/>
					</Box>

					{/* Clickable text dropdown on the right */}
					{!isTabletOrMobile && showActiveUserResource && !isUserManagement && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<GeneralSearchBar />

							<Button
								onClick={handleClick}
								data-testid="user-menu-button"
								sx={{
									color: 'white',
									textTransform: 'none',
									fontWeight: 500,
									display: 'flex',
									alignItems: 'center',
									gap: 0.5,
									padding: 0,
								}}
								endIcon={<ArrowDropDownIcon sx={{ color: 'white', fontSize: 22 }} />}
							>
								{user
									? user.firstName || user.lastName
										? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
										: user.emailAddress || 'User'
									: 'User'}
							</Button>

							<Menu
								anchorEl={anchorEl}
								open={open}
								onClose={() => handleClose()}
								anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
								transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							>
								{shouldShowUserManagement && (
									<MenuItem
										onClick={() => {
											handleClose();
											navigate(`/${USER_MANAGEMENT_URI}`);
										}}
									>
										{USER_MANAGEMENT}
									</MenuItem>
								)}
								<MenuItem onClick={() => setIsUserGuideModalOpen(true)}>User Guide</MenuItem>
								<MenuItem onClick={handleLogout}>Logout</MenuItem>
							</Menu>
						</Box>
					)}
				</Box>
				{showActiveUserResource && <MainNavBar />}
				<AlertBar />
			</AppBar>

			{isUserGuideModalOpen && <UserGuideModal onClose={() => setIsUserGuideModalOpen(false)} />}
		</>
	);
}
